import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, X, Calendar as CalendarIcon, Clock, Ban, RotateCcw,
  AlertTriangle, Phone, Users as UsersIcon, FileCheck2, FileSignature, Video,
} from "lucide-react";
import { authHeaders, API, formatApiErrorDetail } from "./api";

export const APPOINTMENT_TYPES = ["Phone call", "Meeting", "Document check", "Signing", "Other"];
export const APPOINTMENT_LENGTHS = [15, 30, 45, 60, 90];

export const typeIcon = (t) =>
  t === "Phone call" ? Phone
  : t === "Meeting" ? UsersIcon
  : t === "Document check" ? FileCheck2
  : t === "Signing" ? FileSignature
  : t === "Video call" ? Video
  : t === "Other" ? CalendarIcon
  : CalendarIcon;

export const typeStyle = (t) =>
  t === "Phone call" ? "bg-sky-50 text-sky-800 border-sky-200"
  : t === "Meeting" ? "bg-amber-50 text-amber-800 border-amber-200"
  : t === "Document check" ? "bg-indigo-50 text-indigo-700 border-indigo-200"
  : t === "Signing" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
  : t === "Video call" ? "bg-rose-50 text-rose-700 border-rose-200"
  : t === "Other" ? "bg-cream-3 text-forest/70 border-forest/15"
  : "bg-cream-3 text-forest/70 border-forest/15";

export const fmtDMY = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

export const fmtTime = (hhmm) => {
  if (!hhmm) return "";
  return hhmm;
};

const emptyForm = () => {
  const d = new Date();
  const iso = d.toISOString().slice(0, 10);
  return { title: "", date: iso, time: "10:00", length_minutes: 30, type: "Meeting", note: "" };
};

const AppointmentForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || emptyForm());
  useEffect(() => { setForm(initial || emptyForm()); }, [initial]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "length_minutes" ? Number(e.target.value) : e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    if (!form.time) { toast.error("Time is required"); return; }
    onSave(form);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-forest/15 rounded-lg p-5 space-y-4" data-testid="appointment-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Title <span className="text-red-600">*</span></span>
          <input type="text" value={form.title} onChange={update("title")} data-testid="appt-field-title" placeholder="e.g. Document check"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Date (DD/MM/YYYY) <span className="text-red-600">*</span></span>
          <input type="date" value={form.date} onChange={update("date")} data-testid="appt-field-date"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          {form.date && <p className="text-[11px] text-sage mt-1">{fmtDMY(form.date)}</p>}
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Time <span className="text-red-600">*</span></span>
          <input type="time" value={form.time} onChange={update("time")} data-testid="appt-field-time"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Length</span>
          <select value={form.length_minutes} onChange={update("length_minutes")} data-testid="appt-field-length"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            {APPOINTMENT_LENGTHS.map((n) => <option key={n} value={n}>{n} min</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Type</span>
          <select value={form.type} onChange={update("type")} data-testid="appt-field-type"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            {APPOINTMENT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Short note</span>
          <textarea rows={3} value={form.note} onChange={update("note")} data-testid="appt-field-note"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-y" />
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="appt-save-btn" className="btn-gold disabled:opacity-50">
          {saving ? "Saving…" : (initial ? "Save changes" : "Add appointment")}
        </button>
        <button type="button" onClick={onCancel} data-testid="appt-cancel-btn" className="btn-ghost-dark">Cancel</button>
      </div>
    </form>
  );
};

const OverlapDialog = ({ overlaps, onConfirm, onDismiss, saving }) => (
  <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center px-4" data-testid="appt-overlap-dialog">
    <div className="bg-cream max-w-md w-full p-6 rounded-lg shadow-2xl">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 rounded-full">
          <AlertTriangle size={20} strokeWidth={1.8} className="text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-xl text-forest">Time clash</h3>
          <p className="text-[13px] text-sage mt-1">
            This slot clashes with {overlaps.length} other scheduled appointment{overlaps.length > 1 ? "s" : ""}.
            You can still save if you want to.
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-2 max-h-56 overflow-y-auto">
        {overlaps.map((o) => (
          <div key={o.id} className="bg-white border border-forest/15 rounded px-3 py-2 text-[12.5px]" data-testid="appt-overlap-item">
            <p className="font-semibold text-forest">{o.title}</p>
            <p className="text-sage">
              {o.customer_name || "—"} · {fmtDMY(o.date)} · {o.time} · {o.length_minutes} min
            </p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-5 justify-end">
        <button type="button" onClick={onDismiss} data-testid="appt-overlap-cancel" className="btn-ghost-dark">Change time</button>
        <button type="button" onClick={onConfirm} disabled={saving} data-testid="appt-overlap-confirm"
          className="btn-gold disabled:opacity-50">
          {saving ? "Saving…" : "Save anyway"}
        </button>
      </div>
    </div>
  </div>
);

export const CustomerAppointments = ({ customerId, customerName }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming"); // upcoming | past | cancelled | all
  const [editing, setEditing] = useState(null); // null | "new" | appt object
  const [saving, setSaving] = useState(false);
  const [overlapState, setOverlapState] = useState(null); // { overlaps, payload, mode, apptId }

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/admin/customers/${customerId}/appointments`, authHeaders())
      .then(({ data }) => setRows(data.appointments || []))
      .catch(() => toast.error("Could not load appointments"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [customerId]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    if (filter === "upcoming") return rows.filter((r) => r.status === "scheduled" && r.date >= todayIso);
    if (filter === "past") return rows.filter((r) => r.status === "scheduled" && r.date < todayIso);
    if (filter === "cancelled") return rows.filter((r) => r.status === "cancelled");
    return rows;
  }, [rows, filter, todayIso]);

  const doSave = async (payload, { force = false } = {}) => {
    setSaving(true);
    try {
      if (editing === "new") {
        const url = `${API}/admin/customers/${customerId}/appointments${force ? "?force=true" : ""}`;
        const r = await axios.post(url, payload, authHeaders());
        if (r.status === 409 && r.data?.detail === "overlap") {
          setOverlapState({ overlaps: r.data.overlaps || [], payload, mode: "create" });
          return;
        }
        toast.success("Appointment added");
      } else {
        const url = `${API}/admin/customers/${customerId}/appointments/${editing.id}${force ? "?force=true" : ""}`;
        const r = await axios.put(url, payload, authHeaders());
        if (r.status === 409 && r.data?.detail === "overlap") {
          setOverlapState({ overlaps: r.data.overlaps || [], payload, mode: "update", apptId: editing.id });
          return;
        }
        toast.success("Appointment updated");
      }
      setOverlapState(null);
      setEditing(null);
      reload();
    } catch (e) {
      // axios throws for 4xx by default — intercept 409.
      if (e.response?.status === 409 && e.response.data?.detail === "overlap") {
        setOverlapState({
          overlaps: e.response.data.overlaps || [],
          payload,
          mode: editing === "new" ? "create" : "update",
          apptId: editing === "new" ? null : editing.id,
        });
        return;
      }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save appointment");
    } finally {
      setSaving(false);
    }
  };

  const confirmOverlap = async () => {
    if (!overlapState) return;
    const { payload, mode, apptId } = overlapState;
    setSaving(true);
    try {
      if (mode === "create") {
        await axios.post(`${API}/admin/customers/${customerId}/appointments?force=true`, payload, authHeaders());
        toast.success("Appointment added");
      } else {
        await axios.put(`${API}/admin/customers/${customerId}/appointments/${apptId}?force=true`, payload, authHeaders());
        toast.success("Appointment updated");
      }
      setOverlapState(null);
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save appointment");
    } finally {
      setSaving(false);
    }
  };

  const cancelAppt = async (a) => {
    if (!window.confirm(`Cancel "${a.title}"?`)) return;
    try {
      await axios.post(`${API}/admin/customers/${customerId}/appointments/${a.id}/cancel`, {}, authHeaders());
      toast.success("Appointment cancelled");
      reload();
    } catch { toast.error("Could not cancel"); }
  };

  const reopenAppt = async (a) => {
    try {
      await axios.post(`${API}/admin/customers/${customerId}/appointments/${a.id}/reopen`, {}, authHeaders());
      toast.success("Appointment restored");
      reload();
    } catch { toast.error("Could not restore"); }
  };

  if (loading) return <p className="text-sage">Loading appointments…</p>;

  return (
    <div className="space-y-5" data-testid="customer-appointments-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { v: "upcoming", label: "Upcoming" },
            { v: "past", label: "Past" },
            { v: "cancelled", label: "Cancelled" },
            { v: "all", label: "All" },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              data-testid={`appts-filter-${f.v}`}
              className={`px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
                filter === f.v
                  ? "bg-forest text-cream border-forest"
                  : "bg-white border-forest/20 text-forest/70 hover:border-forest"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {editing !== "new" && !editing && (
          <button type="button" onClick={() => setEditing("new")} data-testid="appts-add-btn"
            className="inline-flex items-center gap-2 bg-forest hover:bg-forest-soft text-cream px-4 py-2.5 rounded text-[11px] font-bold tracking-[0.2em] uppercase transition-colors">
            <Plus size={14} strokeWidth={2.2} /> Add appointment
          </button>
        )}
      </div>

      {(editing === "new" || (editing && typeof editing === "object")) && (
        <AppointmentForm
          initial={editing === "new" ? null : editing}
          onCancel={() => { setEditing(null); setOverlapState(null); }}
          onSave={doSave}
          saving={saving}
        />
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-forest/10 rounded-xl p-10 text-center" data-testid="appts-empty">
          <CalendarIcon size={28} strokeWidth={1.4} className="mx-auto text-forest/30 mb-3" />
          <p className="font-serif text-lg text-forest mb-1">
            {filter === "cancelled" ? "No cancelled appointments" :
             filter === "past" ? "No past appointments" :
             filter === "all" ? `No appointments yet for ${customerName}` :
             "No upcoming appointments"}
          </p>
          <p className="text-[13px] text-sage mb-5">
            Add appointments to keep every call, meeting and signing in one place.
          </p>
          {!editing && (
            <button type="button" onClick={() => setEditing("new")} data-testid="appts-empty-add"
              className="btn-gold inline-flex items-center gap-2">
              <Plus size={14} strokeWidth={2.2} /> Add first appointment
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((a, i) => {
            const Icon = typeIcon(a.type);
            const isCancelled = a.status === "cancelled";
            return (
              <div
                key={a.id}
                data-testid={`appt-row-${i + 1}`}
                className={`bg-white border rounded-lg p-4 flex gap-3 items-start transition-opacity ${
                  isCancelled ? "border-forest/10 opacity-55 bg-cream-3/60" : "border-forest/10"
                }`}
              >
                <div className={`mt-0.5 w-9 h-9 rounded-full border flex items-center justify-center shrink-0 ${
                  isCancelled ? "bg-cream-3 border-forest/15 text-forest/40"
                              : "bg-gold-soft/15 border-gold/40 text-gold-dark"
                }`}>
                  <Icon size={16} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className={`font-semibold ${isCancelled ? "line-through text-forest/50" : "text-forest"}`}>{a.title}</p>
                      {a.note && (
                        <p className={`text-[13px] mt-1 whitespace-pre-wrap ${isCancelled ? "text-forest/40" : "text-sage"}`}>{a.note}</p>
                      )}
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded ${typeStyle(a.type)}`}>{a.type}</span>
                        <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-forest/70">
                          <CalendarIcon size={11} strokeWidth={1.8} />
                          {fmtDMY(a.date)} · {fmtTime(a.time)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11.5px] text-forest/60">
                          <Clock size={11} strokeWidth={1.8} />
                          {a.length_minutes} min
                        </span>
                        {isCancelled && (
                          <span className="inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded bg-rose-50 text-rose-700 border-rose-200">Cancelled</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {!isCancelled ? (
                        <>
                          <button type="button" onClick={() => setEditing(a)} data-testid={`appt-edit-${i + 1}`}
                            className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded" title="Edit">
                            <Pencil size={14} strokeWidth={1.8} />
                          </button>
                          <button type="button" onClick={() => cancelAppt(a)} data-testid={`appt-cancel-${i + 1}`}
                            className="p-1.5 text-rose-500/80 hover:text-rose-700 hover:bg-rose-50 rounded" title="Cancel">
                            <Ban size={14} strokeWidth={1.8} />
                          </button>
                        </>
                      ) : (
                        <button type="button" onClick={() => reopenAppt(a)} data-testid={`appt-restore-${i + 1}`}
                          className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded" title="Restore">
                          <RotateCcw size={14} strokeWidth={1.8} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {overlapState && (
        <OverlapDialog
          overlaps={overlapState.overlaps}
          onConfirm={confirmOverlap}
          onDismiss={() => setOverlapState(null)}
          saving={saving}
        />
      )}
    </div>
  );
};
